<template>
  <div
    :class="[
      'pill',
      {
        completed: isCompleted,
      },
    ]"
  >
    <div class="chart-container">
      <FriedIcon v-if="isCompleted" icon="bold-check-mark" />
      <canvas v-show="!isCompleted" ref="chartCanvas" />
    </div>
    <FriedH5>{{ percentageText }}</FriedH5>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import { ArcElement, Chart, PieController } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import type { Ref } from 'vue';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';

Chart.register(PieController, ArcElement, ChartDataLabels);

export default defineComponent({
  props: {
    percentage: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const roundedPercentage = computed(() => Math.round(props.percentage));
    const percentageText = computed(() => `${roundedPercentage.value}% ${t('completed')}`);
    const isCompleted = computed(() => roundedPercentage.value === 100);
    const chartCanvas: Ref<HTMLCanvasElement> = ref(null);
    let chart: Chart<'doughnut'> = null;

    watch(
      () => props.percentage,
      () => {
        if (chart) {
          chart.data.datasets[0].data = [props.percentage, 100 - props.percentage];
          chart.update();
        }
      }
    );

    onMounted(() => {
      chart = new Chart(chartCanvas.value, {
        type: 'doughnut',
        data: {
          labels: ['Completed', 'Not completed'],
          datasets: [
            {
              data: [props.percentage, 100 - props.percentage],
              backgroundColor: ['#04AD82', '#E4EAF1'],
              borderWidth: 0,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
            datalabels: {
              display: false,
            },
          },
          responsive: true,
          maintainAspectRatio: true,
        },
      });
    });

    return {
      isCompleted,
      percentageText,
      chartCanvas,
    };
  },
});
</script>

<style lang="scss" scoped>
.pill {
  display: flex;
  align-items: center;
  gap: var(--spacing-50);
  padding: 0 var(--spacing-75);
  height: 2.5rem;
  border-radius: var(--radius-medium);
  border: 1px solid var(--gray-89);

  &.completed {
    border-color: var(--jade-35);

    h5 {
      color: var(--green-25);
    }
  }

  .chart-container {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      color: var(--jade-35);
    }
  }
}
</style>
